import { Play } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { H6, Subtle } from "@/components/v2/typography";
import { cn } from "@/utils/cn";

interface IProps {
  title?: string;
  description?: string;
  buttonLabel?: string;
  onGenerate?: () => void;
  disabled?: boolean;
  /** Shown on hover while disabled, so it never looks broken (B52). */
  disabledReason?: string;
  isLoading?: boolean;
  className?: string;
}

export const GeneratePayrollCTA = ({
  title,
  description,
  buttonLabel,
  onGenerate,
  disabled,
  disabledReason,
  isLoading,
  className,
}: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  // Resolved in the body: parameter defaults evaluate before the hook runs.
  const titleText = title ?? t("company:payrolls.ready_to_generate_payroll");
  const descriptionText =
    description ?? t("company:payrolls.generate_and_proceed");
  const buttonText = buttonLabel ?? t("company:payrolls.generate_payroll");
  const button = (
    <Button
      variant="primary"
      size="default"
      onClick={onGenerate}
      disabled={disabled}
      isLoading={isLoading}
      className="shrink-0"
    >
      <Play size={14} className="fill-current" />
      {buttonText}
    </Button>
  );
  return (
    <div
      className={cn(
        "flex flex-col md:flex-row items-start md:items-center justify-between gap-4",
        "rounded-xs border border-border bg-white px-5 py-4",
        "bg-linear-to-r from-orange-50/40 via-white to-primary/10",
        className,
      )}
    >
      <div className="flex flex-col gap-1 min-w-0">
        <H6 className="font-semibold">{titleText}</H6>
        <Subtle>{descriptionText}</Subtle>
      </div>

      {disabled && disabledReason ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex shrink-0">{button}</span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{disabledReason}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        button
      )}
    </div>
  );
};
